import type { JSX } from "react";
import { createBrowserRouter, Navigate, useParams } from "react-router";

import {
  LoginRoute,
  RequireAgency,
  RequireAuth,
  RequirePlatformAdmin,
  RequireRole,
  RequireRoleOrPlatformAdmin,
} from "@/app/guards";
import { RouteError } from "@/app/RouteError";
import { PageLoader } from "@/components/ui";
import { ChannelsRedirect } from "@/features/settings/ChannelsRedirect";
import { SettingsIndexRedirect, SettingsLayout } from "@/features/settings/SettingsLayout";
import { PlatformShell } from "@/features/platform/PlatformShell";
import { AppShell } from "@/features/shell/AppShell";
import {
  canEditCrmRecords,
  canOpenBookings,
  canManageAutomations,
  canManageTenantSettings,
  canOpenAiAgent,
  canUseAssistantChat,
  canRunCampaigns,
  canUseInbox,
  canManageTeamAndDistribution,
  canManageDestinations,
  canReadBilling,
  canViewFinanceSettings,
  canViewContractTemplates,
  canViewCustDev,
  canViewReports,
} from "@/lib/permissions";

type PageComponent = () => JSX.Element;

/**
 * Route-level code splitting: a screen's chunk is fetched on the first visit to its
 * route, not in the entry bundle. `load` names the module and `name` the export, so a
 * renamed export fails the typecheck rather than rendering nothing.
 */
function page<K extends string>(
  load: () => Promise<Record<K, PageComponent>>,
  name: K,
): { lazy: () => Promise<{ Component: PageComponent }> } {
  return {
    lazy: async (): Promise<{ Component: PageComponent }> => {
      const module = await load();
      return { Component: module[name] };
    },
  };
}

/**
 * Addresses the backend wrote into notifications before these screens moved. A bell row and a
 * Telegram message keep the address they were sent with, so these stay even after the backend
 * stops writing one; without them the catch-all drops the person on the home page.
 */
export const OLD_ADDRESSES: readonly (readonly [string, string])[] = [
  ["/vazifalar", "/tasks"],
  ["/izohlar/:commentId", "/inbox/comments"],
  ["/sozlamalar/ai", "/ai"],
  ["/sozlamalar/ai/bilim-bazasi", "/ai/knowledge"],
  ["/sozlamalar/ai/sinov-markazi", "/ai/tests"],
  ["/sozlamalar/kanallar", "/settings/channels"],
  ["/sozlamalar/katalog-moslashtirish", "/sozlamalar/katalog"],
  ["/sozlamalar/xavfsizlik", "/sozlamalar?section=audit"],
];

function OldThreadAddress(): JSX.Element {
  const { conversationId } = useParams();
  return <Navigate to={`/inbox?c=${conversationId ?? ""}`} replace />;
}

export const router = createBrowserRouter([
  {
    // Nothing under the routes may end in React Router's developer page (see RouteError).
    errorElement: <RouteError />,
    // Shown while the first lazy route's chunk loads; without it React Router warns on
    // every cold start and renders nothing in the gap.
    hydrateFallbackElement: <PageLoader />,
    children: [
  { path: "/kirish", element: <LoginRoute /> },
  // The client's offer page. Open by link (§3.8), so outside RequireAuth and outside the shell.
  {
    path: "/p/:token",
    ...page(() => import("@/features/proposals/PublicProposalPage"), "PublicProposalPage"),
  },
  // The one-time link `create_tenant` prints. Outside RequireAuth on purpose: the
  // only person who ever follows it cannot sign in yet.
  {
    path: "/parolni-tiklash/:uid/:token",
    ...page(() => import("@/features/auth/PasswordSetPage"), "PasswordSetPage"),
  },
  // `Parolni unutdim` and the link it mails. Outside RequireAuth: nobody who needs
  // these can sign in yet.
  { path: "/forgot-password", ...page(() => import("@/features/auth/ForgotPasswordPage"), "ForgotPasswordPage") },
  {
    path: "/reset-password/:uid/:token",
    ...page(() => import("@/features/auth/ResetPasswordPage"), "ResetPasswordPage"),
  },
  // Pages a client opens from a link. No shell and no account: they sit outside RequireAuth.
  { path: "/s/:token", ...page(() => import("@/features/public/SignPage"), "SignPage") },
  { path: "/pay/:token", ...page(() => import("@/features/public/PayPage"), "PayPage") },
  { path: "/r/:token", ...page(() => import("@/features/public/ReviewPage"), "ReviewPage") },
  {
    element: <RequireAuth />,
    children: [
      {
        // The wizard stands on its own (K3): no sidebar until the agency is set up. Still an
        // agency's screen, and its API is the owner's (CanManageTenantSettings), so the screen is too.
        element: (
          <RequireAgency>
            <RequireRole allow={canManageTenantSettings} />
          </RequireAgency>
        ),
        children: [
          { path: "/onboarding", ...page(() => import("@/features/agency/OnboardingPage"), "OnboardingPage") },
        ],
      },
      {
        // The platform panel (Kapitan staff), in its own frame: nothing of an agency's shell.
        element: <RequirePlatformAdmin />,
        children: [
          {
            element: <PlatformShell />,
            children: [
              { path: "/platforma", element: <Navigate to="/platforma/tenantlar" replace /> },
              { path: "/platforma/tenantlar", ...page(() => import("@/features/platform/PlatformPages"), "PlatformTenantsPage") },
              { path: "/platforma/tenant-yaratish", ...page(() => import("@/features/platform/PlatformPages"), "PlatformCreateTenantPage") },
              { path: "/platforma/tenantlar/:tenantId", ...page(() => import("@/features/platform/PlatformPages"), "PlatformTenantPage") },
              { path: "/platforma/tariflar", ...page(() => import("@/features/platform/PlatformPages"), "PlatformTariffsPage") },
              { path: "/platforma/demo-sorovlar", ...page(() => import("@/features/platform/PlatformPages"), "PlatformDemoPage") },
              { path: "/platforma/kredit-sorovlari", ...page(() => import("@/features/platform/PlatformPages"), "PlatformCreditsPage") },
              { path: "/platforma/audit", ...page(() => import("@/features/platform/PlatformPages"), "PlatformAuditPage") },
              { path: "/platforma/provayderlar", ...page(() => import("@/features/platform/PlatformPages"), "PlatformProvidersPage") },
              { path: "/platforma/kanallar", ...page(() => import("@/features/platform/PlatformStatusPages"), "PlatformChannelsPage") },
              { path: "/platforma/ai-sarfi", ...page(() => import("@/features/platform/PlatformStatusPages"), "PlatformAiSpendPage") },
              { path: "/platforma/otel-normalizatsiya", ...page(() => import("@/features/platform/PlatformStatusPages"), "PlatformHotelPairsPage") },
              { path: "/platforma/aeroportlar", ...page(() => import("@/features/platform/PlatformPages"), "PlatformAirportsPage") },
              { path: "/platforma/yonalishlar", ...page(() => import("@/features/platform/PlatformCataloguePages"), "PlatformDestinationsPage") },
              { path: "/platforma/kataloglar", ...page(() => import("@/features/platform/PlatformCataloguePages"), "PlatformCataloguesPage") },
              { path: "/platforma/tizim", ...page(() => import("@/features/platform/PlatformSystemPage"), "PlatformSystemPage") },
              { path: "/platforma/support", ...page(() => import("@/features/platform/PlatformPages"), "PlatformSupportPage") },
              { path: "/platforma/integratsiyalar", ...page(() => import("@/features/integrations/IntegrationsPages"), "PlatformIntegrationsPage") },
            ],
          },
        ],
      },
      {
        // Every screen under the shell is an agency's; a platform admin with no agency is sent
        // to the panel from any of them (D202).
        element: (
          <RequireAgency>
            <AppShell />
          </RequireAgency>
        ),
        children: [
          { path: "/", ...page(() => import("@/features/dashboard/DashboardPage"), "DashboardPage") },
          { path: "/profile", ...page(() => import("@/features/profile/ProfilePage"), "ProfilePage") },
          {
            // Suhbatlar: three panes on desktop, a full-screen thread on a phone. Egasi,
            // Menejer and Operator only; Moliyachi has no inbox at all.
            element: <RequireRole allow={canUseInbox} />,
            children: [
              { path: "/inbox", ...page(() => import("@/features/inbox/InboxPage"), "InboxPage") },
              // The approvals tab is gone: a draft waits inside its thread (09 §7, 4.8). Old links
              // land on the inbox filtered to the threads with a draft waiting.
              {
                path: "/inbox/approvals",
                element: <Navigate to="/inbox?filter=ai" replace />,
              },
              // Murojaatlar is no section any more (D613): it is Suhbatlar' "Yangi" filter.
              { path: "/murojaatlar", element: <Navigate to="/inbox?filter=new" replace /> },
              {
                path: "/inbox/comments",
                ...page(() => import("@/features/inbox/CommentsPage"), "CommentsPage"),
              },
            ],
          },
          // The Meta consent callback names `/settings/channels`. Kanallar lives inside
          // `/sozlamalar`, so the redirect keeps the query (`instagram=…`) and opens that section.
          { path: "/settings/channels", element: <ChannelsRedirect /> },
          { path: "/moliya", ...page(() => import("@/features/finance/FinancePage"), "FinancePage") },
          { path: "/bitimlar", ...page(() => import("@/features/deals/DealsPage"), "DealsPage") },
          {
            path: "/bitimlar/:dealId",
            ...page(() => import("@/features/deals/DealsPage"), "DealDetailPage"),
          },
          { path: "/tasks", ...page(() => import("@/features/tasks/TasksPage"), "TasksPage") },
          { path: "/mijozlar", ...page(() => import("@/features/clients/ClientsPage"), "ClientsPage") },
          // The chat-yordamchi (TZ §7.7): the moliyachi's menu has no such row (K3).
          {
            element: <RequireRole allow={canUseAssistantChat} />,
            children: [
              {
                path: "/yordamchi",
                ...page(() => import("@/features/ai/AssistantChatPage"), "AssistantChatPage"),
              },
            ],
          },
          // Creating a client is a CRM write (`can_edit_crm_records`): the moliyachi reads the
          // pipeline but never adds to it, so the form is refused on the route, as on the API.
          {
            element: <RequireRole allow={canEditCrmRecords} />,
            children: [
              {
                path: "/mijozlar/yangi",
                ...page(() => import("@/features/clients/NewClientPage"), "NewClientPage"),
              },
            ],
          },
          {
            path: "/hujjat-talablari",
            ...page(
              () => import("@/features/clients/DocumentRequirementsPage"),
              "DocumentRequirementsPage",
            ),
          },
          // The rules belong to the owner (Q69), the campaigns to whoever may send in bulk (D215);
          // refused on the route, so the page never asks the API for what it cannot show.
          {
            element: <RequireRole allow={canManageAutomations} />,
            children: [
              {
                path: "/automatizatsiya",
                ...page(() => import("@/features/automations/AutomationsPage"), "AutomationsPage"),
              },
            ],
          },
          {
            element: <RequireRole allow={canRunCampaigns} />,
            children: [
              {
                path: "/kampaniyalar",
                ...page(() => import("@/features/campaigns/CampaignsPage"), "CampaignsPage"),
              },
            ],
          },
          {
            path: "/mijozlar/:clientId",
            ...page(() => import("@/features/clients/ClientsPage"), "ClientCardPage"),
          },
          // K3 refuses the moliyachi this route: selling is not their work.
          {
            element: <RequireRole allow={canEditCrmRecords} />,
            children: [
              {
                path: "/tur-qidiruvi",
                ...page(() => import("@/features/search/SearchPage"), "SearchPage"),
              },
              { path: "/search", ...page(() => import("@/features/search/SearchPage"), "SearchPage") },
            ],
          },
          // Bronlar (§10): every role but the moliyachi.
          {
            element: <RequireRole allow={canOpenBookings} />,
            children: [{ path: "/bronlar", ...page(() => import("@/features/bookings/BookingsPage"), "BookingsPage") }],
          },
          // Hisobotlar: the gate is the reporting roles. Each tab's own role check is in
          // `reportTabsFor`, and the API refuses the rest.
          {
            element: <RequireRole allow={canViewReports} />,
            children: [
              {
                path: "/hisobotlar",
                ...page(() => import("@/features/reports/ReportsLayout"), "ReportsLayout"),
                children: [
                  { path: "sotuv", ...page(() => import("@/features/reports/SummaryPages"), "SalesTab") },
                  { path: "operatorlar", ...page(() => import("@/features/reports/SummaryPages"), "OperatorsTab") },
                  { path: "manbalar", ...page(() => import("@/features/reports/SummaryPages"), "SourcesTab") },
                  { path: "moliya", ...page(() => import("@/features/reports/SummaryPages"), "FinanceTab") },
                  { path: "portret", ...page(() => import("@/features/reports/SummaryPages"), "PortraitTab") },
                  { path: "dinamika", ...page(() => import("@/features/reports/ReportPages"), "MoliyaReportPage") },
                  { path: "lidlar", ...page(() => import("@/features/reports/ReportPages"), "LeadsReportPage") },
                  { path: "voronka", ...page(() => import("@/features/reports/ReportPages"), "FunnelReportPage") },
                  { path: "kanallar", ...page(() => import("@/features/reports/ReportPages"), "ChannelsReportPage") },
                  { path: "javob-tezligi", ...page(() => import("@/features/reports/ReportPages"), "ResponsesReportPage") },
                  { path: "marja", ...page(() => import("@/features/reports/ReportPages"), "MarginReportPage") },
                  { path: "qarzdorlar", ...page(() => import("@/features/reports/ReportPages"), "DebtorsReportPage") },
                  { path: "operatorlar-batafsil", ...page(() => import("@/features/reports/ReportPages"), "OperatorsReportPage") },
                  { path: "ai", ...page(() => import("@/features/reports/ReportPages"), "AiReportPage") },
                  // The owner alone (§8.6), so the route carries its own gate on top of the reports one.
                  {
                    element: <RequireRole allow={canViewCustDev} />,
                    children: [
                      { path: "custdev", ...page(() => import("@/features/reports/ReportPages"), "CustDevReportPage") },
                    ],
                  },
                  { path: "filiallar", ...page(() => import("@/features/reports/ReportPages"), "BranchesReportPage") },
                ],
              },
            ],
          },
          // The personal report: every role reads its own figures, so it sits outside the gate.
          { path: "/hisobotlar/mening", ...page(() => import("@/features/reports/SummaryPages"), "PersonalReportPage") },
          // Sozlamalar has its own sidebar (spec §2, §11): seven sections. The layout is open to
          // every role that has one of them; each section's route carries the gate its screen
          // needs, so a hidden link and a refused page are decided by the same predicate.
          // Sozlamalar (K3): one menu beside every section. Absolute paths under a pathless layout, so
          // `/shablonlar` and `/sozlamalar/tarif` keep their addresses inside it.
          {
            element: <SettingsLayout />,
            children: [
              { path: "/sozlamalar", element: <SettingsIndexRedirect /> },
              {
                element: <RequireRoleOrPlatformAdmin allow={canManageTenantSettings} />,
                children: [
                  { path: "/sozlamalar/katalog", ...page(() => import("@/features/settings/catalog/CatalogMappingPage"), "CatalogMappingPage") },
                  { path: "/sozlamalar/turoperatorlar", ...page(() => import("@/features/settings/operators/OperatorConnectionsPage"), "OperatorConnectionsPage") },
                ],
              },
              {
                element: <RequireRole allow={canManageTenantSettings} />,
                children: [
                  { path: "/sozlamalar/agentlik", ...page(() => import("@/features/settings/SettingsPage"), "SettingsPage") },
                  { path: "/sozlamalar/voronka", ...page(() => import("@/features/settings/SettingsPage"), "FunnelSettingsPage") },
                  { path: "/sozlamalar/audit", ...page(() => import("@/features/settings/SettingsPage"), "AuditSettingsPage") },
                  { path: "/sozlamalar/narx", ...page(() => import("@/features/settings/NarxSettingsPage"), "NarxSettingsPage") },
                  { path: "/sozlamalar/kanallar", ...page(() => import("@/features/settings/K3Sections"), "ChannelsSettingsPage") },
                  { path: "/sozlamalar/bildirishnomalar", ...page(() => import("@/features/settings/K3Sections"), "NotificationsSettingsPage") },
                  { path: "/sozlamalar/tizim", ...page(() => import("@/features/settings/K3Sections"), "SystemHealthPage") },
                  { path: "/sozlamalar/import", ...page(() => import("@/features/agency/ImportPage"), "ImportPage") },
                  { path: "/sozlamalar/integratsiyalar", ...page(() => import("@/features/integrations/IntegrationsPages"), "AgencyIntegrationsPage") },
                ],
              },
              // TZ §17.14: Moliya is the owner's and the moliyachi's; Billing too, read-only for the
              // moliyachi. The screens drop what that role's API would refuse.
              {
                element: <RequireRole allow={canViewFinanceSettings} />,
                children: [
                  { path: "/sozlamalar/moliya", ...page(() => import("@/features/settings/K3Sections"), "FinanceSettingsPage") },
                ],
              },
              {
                element: <RequireRole allow={canReadBilling} />,
                children: [
                  { path: "/sozlamalar/tarif", ...page(() => import("@/features/agency/BillingPage"), "BillingPage") },
                ],
              },
              {
                element: <RequireRole allow={canManageTeamAndDistribution} />,
                children: [
                  { path: "/sozlamalar/jamoa", ...page(() => import("@/features/settings/TeamSettingsPage"), "TeamSettingsPage") },
                  { path: "/sozlamalar/taqsimlash", ...page(() => import("@/features/settings/DistributionRulesPage"), "DistributionRulesPage") },
                  { path: "/sozlamalar/sla", ...page(() => import("@/features/settings/K3Sections"), "SlaSettingsPage") },
                  { path: "/sozlamalar/hujjatlar", ...page(() => import("@/features/clients/DocumentRequirementsPage"), "DocumentRequirementsPage") },
                ],
              },
              {
                element: <RequireRole allow={canManageDestinations} />,
                children: [
                  { path: "/sozlamalar/yonalishlar", ...page(() => import("@/features/settings/DestinationSettingsPage"), "DestinationSettingsPage") },
                ],
              },
              {
                element: <RequireRole allow={canViewContractTemplates} />,
                children: [
                  { path: "/shablonlar", ...page(() => import("@/features/settings/K3Sections"), "TemplatesSettingsPage") },
                ],
              },
            ],
          },
          // The platform's own integration keys (platform admin).
          // The AI agent section. Moliyachi is refused outright; an operator's landing is
          // decided inside the screens (the posts), and every page is checked by the API.
          {
            element: <RequireRole allow={canOpenAiAgent} />,
            children: [
              {
                path: "/ai",
                ...page(() => import("@/features/ai/AiLayout"), "AiSectionLayout"),
                children: [
                  { index: true, ...page(() => import("@/features/ai/AiOverviewPage"), "AiOverviewPage") },
                  { path: "files", ...page(() => import("@/features/ai/AiFilesPage"), "AiFilesPage") },
                  { path: "knowledge", ...page(() => import("@/features/ai/AiKnowledgePages"), "AiKnowledgePage") },
                  { path: "tasks", ...page(() => import("@/features/ai/AiTasksPages"), "AiTasksPage") },
                  { path: "tasks/:code", ...page(() => import("@/features/ai/AiTasksPages"), "AiTaskDetailPage") },
                  { path: "journal", ...page(() => import("@/features/ai/AiJournalPages"), "AiJournalPage") },
                  { path: "playground", ...page(() => import("@/features/ai/AiPlaygroundPage"), "AiPlaygroundPage") },
                  { path: "tests", ...page(() => import("@/features/ai/AiTestsPage"), "AiTestsPage") },
                  { path: "memory", ...page(() => import("@/features/ai/AiJournalPages"), "AiMemoryPage") },
                  { path: "posts", ...page(() => import("@/features/ai/AiPostsPage"), "AiPostsPage") },
                  { path: "sozlamalar", ...page(() => import("@/features/ai/AiSettingsPage"), "AiSettingsPage") },
                ],
              },
            ],
          },
          // Kapitan staff only. Loaded on demand so the kit stays out of the main bundle.
          {
            element: <RequirePlatformAdmin />,
            children: [
              {
                path: "/ui-kit",
                ...page(() => import("@/features/ui-kit/UiKitPage"), "UiKitPage"),
              },
            ],
          },
        ],
      },
    ],
  },
  ...OLD_ADDRESSES.map(([path, to]) => ({ path, element: <Navigate to={to} replace /> })),
  { path: "/suhbatlar/:conversationId", element: <OldThreadAddress /> },
  { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
